/**
 * TaskRow
 *
 * Renders a single task within the initial review task list.
 * Includes inline editing for assignee, due date, priority, and status.
 * Shows deliverable match indicators as a sub-row when matches exist.
 */
import { Calendar, Check, Flag, User } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { DeliverableSchemaRead } from '@/services/api/deliverablesServicev2';
import type { Task } from '@/services/types/client-intake-types';
import { CanonicalTaskStatus } from '@/services/types/task-types';
import { formatCompactDate } from '@/utils/formatting';
import { getDaysDiffInNY } from '@/utils/taskDateUtils';

import { CallPrepToggle } from './CallPrepToggle';
import { DeliverableMatchIndicator } from './DeliverableMatchIndicator';

export interface TaskRowProps {
  task: Task;
  staffUsers: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  }[];
  onAssigneeChange: (taskId: string, userId: string | null) => Promise<void>;
  onDueDateChange: (taskId: string, date: string | null) => Promise<void>;
  onPriorityChange: (taskId: string, order: number) => Promise<void>;
  onStatusChange: (
    taskId: string,
    newStatus: CanonicalTaskStatus,
  ) => Promise<void>;
  onTaskClick: (taskId: string) => void;
  toggleInCallPrep?: (sourceId: string, text: string) => void;
  isInCallPrep?: boolean;
  taskDeliverables?: DeliverableSchemaRead[];
  onDocumentClick?: (artifactId: string) => void;
}

const getInitials = (firstName: string, lastName: string) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

export const TaskRow = ({
  task,
  staffUsers,
  onAssigneeChange,
  onDueDateChange,
  onPriorityChange,
  onStatusChange,
  onTaskClick,
  toggleInCallPrep,
  isInCallPrep,
  taskDeliverables,
  onDocumentClick,
}: TaskRowProps) => {
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleAssigneeChange = useCallback(
    async (userId: string | null) => {
      setIsUpdating(true);
      try {
        await onAssigneeChange(task.id, userId);
        setAssigneeOpen(false);
      } finally {
        setIsUpdating(false);
      }
    },
    [task.id, onAssigneeChange],
  );

  const handleDateChange = useCallback(
    async (date: Date | undefined) => {
      setIsUpdating(true);
      try {
        const dateStr = date ? date.toISOString().split('T')[0] : null;
        await onDueDateChange(task.id, dateStr);
        setDateOpen(false);
      } finally {
        setIsUpdating(false);
      }
    },
    [task.id, onDueDateChange],
  );

  // Simplified priority: high (order < 0) or normal (order >= 0)
  const isHighPriority = (task.order ?? 0) < 0;

  const handlePriorityClick = useCallback(async () => {
    setIsUpdating(true);
    try {
      // Toggle between normal (0) and high (-1)
      const newOrder = isHighPriority ? 0 : -1;
      await onPriorityChange(task.id, newOrder);
    } finally {
      setIsUpdating(false);
    }
  }, [task.id, isHighPriority, onPriorityChange]);

  const isCompleted =
    task.status?.canonical_status === CanonicalTaskStatus.COMPLETED;
  const isNotApplicable =
    task.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE;
  const isBlocked =
    task.status?.canonical_status === CanonicalTaskStatus.BLOCKED;
  const isResolved = isCompleted || isNotApplicable;

  // Only show red for critically overdue (>3 days past due) - compared in NY timezone
  const isCriticallyOverdue = useMemo(() => {
    if (!task.due_date || isResolved) return false;
    const daysDiff = getDaysDiffInNY(task.due_date);
    return daysDiff !== null && daysDiff < -3;
  }, [task.due_date, isResolved]);

  const handleNAToggle = useCallback(async () => {
    setIsUpdating(true);
    try {
      await onStatusChange(
        task.id,
        isNotApplicable
          ? CanonicalTaskStatus.PENDING
          : CanonicalTaskStatus.NOT_APPLICABLE,
      );
    } finally {
      setIsUpdating(false);
    }
  }, [task.id, isNotApplicable, onStatusChange]);

  const handleDoneToggle = useCallback(async () => {
    setIsUpdating(true);
    try {
      await onStatusChange(
        task.id,
        isCompleted
          ? CanonicalTaskStatus.PENDING
          : CanonicalTaskStatus.COMPLETED,
      );
    } finally {
      setIsUpdating(false);
    }
  }, [task.id, isCompleted, onStatusChange]);

  return (
    <div
      className={cn(
        'group bg-white dark:bg-zinc-900',
        isNotApplicable && 'opacity-50',
        isCompleted && 'opacity-70',
      )}
    >
      {/* Main task row */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          {/* Status Buttons: N/A and Complete */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={handleNAToggle}
              disabled={isUpdating}
              className={cn(
                'inline-flex items-center justify-center rounded px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
                isNotApplicable
                  ? 'text-zinc-700 dark:text-zinc-300 bg-zinc-200 dark:bg-zinc-700'
                  : 'text-zinc-500 dark:text-zinc-500 border border-zinc-200 dark:border-zinc-700 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800',
              )}
              aria-label={
                isNotApplicable
                  ? 'Remove N/A status'
                  : 'Mark as not applicable'
              }
            >
              N/A
            </button>
            <button
              type="button"
              onClick={handleDoneToggle}
              disabled={isUpdating}
              className={cn(
                'inline-flex items-center gap-1 justify-center rounded px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
                isCompleted
                  ? 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30'
                  : 'text-zinc-500 dark:text-zinc-500 border border-zinc-200 dark:border-zinc-700 hover:text-green-700 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20',
              )}
              aria-label={
                isCompleted ? 'Remove completed status' : 'Mark as complete'
              }
            >
              <Check className="h-3 w-3" />
              Complete
            </button>
          </div>

          {/* Task Name - Clickable */}
          <button
            type="button"
            onClick={() => onTaskClick(task.id)}
            className={cn(
              'text-xs font-medium truncate text-left hover:text-blue-600 dark:hover:text-blue-400 transition-colors',
              isResolved
                ? 'text-zinc-500 dark:text-zinc-500 line-through'
                : 'text-zinc-900 dark:text-white',
            )}
          >
            {task.name}
          </button>

          {isBlocked && (
            <span className="inline-flex rounded bg-red-100 dark:bg-red-900/30 px-1.5 py-0.5 text-xs font-medium text-red-600 dark:text-red-400">
              Blocked
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* Priority Button - only show flag if high priority */}
          {isHighPriority && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePriorityClick}
              disabled={isUpdating || isResolved}
              className="h-7 px-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="High priority - click to remove"
            >
              <Flag className="h-3.5 w-3.5 fill-current" />
            </Button>
          )}
          {!isHighPriority && !isResolved && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePriorityClick}
              disabled={isUpdating}
              className="h-7 px-2 text-zinc-400 dark:text-zinc-600 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Normal priority - click to mark as high"
            >
              <Flag className="h-3.5 w-3.5" />
            </Button>
          )}

          {/* Assignee Dropdown */}
          <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={isUpdating || isResolved}
                className="h-7 gap-1 px-2 text-xs font-medium"
              >
                <User className="h-3.5 w-3.5" />
                {task.assignee ? (
                  <span>
                    {getInitials(
                      task.assignee.first_name,
                      task.assignee.last_name,
                    )}
                  </span>
                ) : (
                  <span>—</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[180px] p-0" align="end">
              <Command>
                <CommandInput
                  placeholder="Search..."
                  className="h-8 text-xs"
                />
                <CommandList>
                  <CommandEmpty>No users found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => handleAssigneeChange(null)}
                      className="cursor-pointer text-xs"
                    >
                      <Check
                        className={cn(
                          'mr-1.5 h-3 w-3',
                          !task.assignee ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span>Unassigned</span>
                    </CommandItem>
                    {staffUsers.map((user) => (
                      <CommandItem
                        key={user.id}
                        onSelect={() => handleAssigneeChange(user.id)}
                        className="cursor-pointer text-xs"
                      >
                        <Check
                          className={cn(
                            'mr-1.5 h-3 w-3',
                            task.assignee?.id === user.id
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        <span>
                          {user.first_name} {user.last_name}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Due Date Picker */}
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={isUpdating || isResolved}
                className={cn(
                  'h-7 gap-1 px-2 text-xs font-medium',
                  isCriticallyOverdue && 'text-red-600 dark:text-red-400',
                )}
              >
                <Calendar className="h-3.5 w-3.5" />
                <span>{formatCompactDate(task.due_date) || '—'}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <CalendarComponent
                mode="single"
                selected={
                  task.due_date ? new Date(task.due_date) : undefined
                }
                onSelect={handleDateChange}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {/* Call Prep Toggle - far right, hidden for resolved tasks */}
          {toggleInCallPrep && !isResolved && (
            <CallPrepToggle
              sourceId={`task_${task.id}`}
              callPrepText={`Discuss task: ${task.name}`}
              isActive={isInCallPrep ?? false}
              onToggle={toggleInCallPrep}
            />
          )}
        </div>
      </div>

      {/* Deliverable match sub-row — only renders if matches exist */}
      {taskDeliverables && taskDeliverables.length > 0 && (
        <DeliverableMatchIndicator
          taskDeliverables={taskDeliverables}
          onViewDocument={onDocumentClick}
        />
      )}
    </div>
  );
};
